import {
  DocumentNode,
  MutationFunctionOptions,
  MutationHookOptions,
  useMutation as useApolloMutation,
  FetchResult,
  OperationVariables,
  DefaultContext,
  ApolloCache,
} from '@apollo/client';
import first from 'lodash/first';
import keys from 'lodash/keys';
import some from 'lodash/some';
import each from 'lodash/each';
import merge from 'lodash/merge';
import lodashDebounce from 'lodash/debounce';
import { enqueueSnackbar } from 'notistack';
import { useRef } from 'react';

export interface Debounce {
  leading?: boolean;
  trailing?: boolean;
  timeout: number;
}

interface MutationCustomConfig {
  onCompleted?: (args: any) => void;
  onNoErrorsCompleted?: (args: any) => void;
  onSomeErrorsCompleted?: (args: any) => void;
  debounce?: Debounce;
}
export type MutationConfig<
  TData = any,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
> = Omit<
  MutationHookOptions<TData, TVariables, TContext, TCache>,
  'onCompleted' | 'onError'
> &
  MutationCustomConfig;

const useDebouncedMutation = <
  TData = any,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
>(
  f: (
    opts: MutationFunctionOptions<TData, TVariables, TContext, TCache>
  ) => Promise<FetchResult<TData>>,
  config?: Debounce
) => {
  const defaultOpts = { timeout: 1000, leading: true, trailing: false };
  const opts = merge(defaultOpts, config);
  const { timeout, leading, trailing } = opts;
  const withDebounce = lodashDebounce((args) => f(args), timeout, {
    leading,
    trailing,
  });
  const debouncedFunction = useRef(withDebounce);
  return debouncedFunction.current;
};

export const useMutation = <
  TData = any,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
>(
  mutation: DocumentNode,
  config: MutationConfig<TData, TVariables, TContext, TCache> = {}
): [(args: { variables: TVariables }) => void, { loading: boolean }] => {
  const {
    onCompleted,
    onNoErrorsCompleted,
    onSomeErrorsCompleted,
    debounce,
    ...rest
  } = config;

  const onCompletedWithErrorHandling = (args: any) => {
    const mutationName = first(keys(args)) || '';
    const mutationResponse = args[mutationName];

    if (some(mutationResponse?.errors)) {
      // there are errors
      if (onSomeErrorsCompleted) {
        onSomeErrorsCompleted(args);
      } else {
        each(mutationResponse.errors, (error) => {
          enqueueSnackbar(error.message, { variant: 'error' });
        });
      }
    } else if (onNoErrorsCompleted) {
      onNoErrorsCompleted(args);
    }
    if (onCompleted) {
      onCompleted(args);
    }
  };

  const onTopLevelError = (error: any) => {
    if (error.message === 'Unauthorized') {
      window.location.href = '/users/sign_in';
    } else {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const [m, other] = useApolloMutation<TData, TVariables, TContext, TCache>(
    mutation,
    {
      onCompleted: onCompletedWithErrorHandling,
      onError: onTopLevelError,
      ...rest,
    }
  );

  const debouncedM = useDebouncedMutation<TData, TVariables, TContext, TCache>(
    m,
    debounce
  );
  return [debouncedM, other];
};
